import {
  Alert,
  Box,
  FormControl,
  FormLabel,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { FormProvider } from "react-hook-form";
import { LoginRequestDto } from "@/dto/AdmDto";
import useLoginForm from "./useLoginForm";

/**
 * 로그인 폼 컴포넌트 Props
 */
export type LoginFormProps = {
  /**
   * 에러 여부
   * @type {boolean}
   * @default false
   */
  error?: boolean;
  /**
   * 에러 메시지
   * @type {string}
   */
  errorMessage?: string;
  /**
   * 로딩 여부
   * @type {boolean}
   * @default false
   */
  isLoading?: boolean;
  /**
   * 로그인 데이터 전달 함수
   * @param {LoginRequestDto} data
   * @returns
   */
  onSubmit: (data: LoginRequestDto) => void;
};

/**
 * 로그인 폼 컴포넌트
 *
 * @description
 * - 아이디와 비밀번호를 입력받는 폼 컴포넌트입니다.
 * - Validation 체크까지만 진행 하며, 실제 로그인은 로그인 페이지에서 처리를 진행합니다.
 * - 관심사 분리를 위한 컴포넌트로, 로그인 페이지에서 사용합니다.
 * @returns {JSX.Element}
 */
export const LoginForm = (props: LoginFormProps) => {
  const { isLoading = false, error = false, errorMessage, onSubmit } = props;
  const { methods, handleSubmit } = useLoginForm(onSubmit);
  const {
    formState: { errors },
  } = methods;

  return (
    <FormProvider {...methods}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: {
            xs: "100%",
            md: 360,
          },
          gap: 4,
        }}
      >
        <div>
          <Typography component={"h1"} variant={"h5"} fontWeight={"bold"}>
            관리자 로그인
          </Typography>
          <Typography variant={"body1"} color={"grey"}>
            STEP AI Super Admin
          </Typography>
        </div>
        {error && (
          <Alert variant="filled" color="error">
            {errorMessage}
          </Alert>
        )}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            gap: 2,
          }}
        >
          <FormControl fullWidth>
            <FormLabel>아이디</FormLabel>
            <TextField
              {...methods.register("admId")}
              variant="outlined"
              color="primary"
              size="small"
              placeholder="아이디를 입력해주세요"
              disabled={isLoading}
              error={!!errors.admId}
              helperText={errors.admId?.message}
            />
          </FormControl>
          <FormControl fullWidth>
            <FormLabel>비밀번호</FormLabel>
            <TextField
              {...methods.register("pwd")}
              type="password"
              variant="outlined"
              color="primary"
              size="small"
              placeholder="비밀번호를 입력해주세요"
              disabled={isLoading}
              error={!!errors.pwd}
              helperText={errors.pwd?.message}
            />
          </FormControl>
        </Box>
        <LoadingButton
          variant="contained"
          color="primary"
          fullWidth
          disableElevation
          loading={isLoading}
          onClick={handleSubmit}
        >
          로그인
        </LoadingButton>
      </Box>
    </FormProvider>
  );
};
